import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

/**
 * The creator release stepper's STATE words — done, not done, and why Launch
 * is closed.
 *
 * These are new because the stepper used to have no state words at all: the
 * ✓ was an `aria-hidden` icon, so "this step is finished" was a fact only a
 * sighted user could read, and it was the WRONG fact besides — `completed`
 * was computed from `activeStep`, i.e. from which panel is showing. A step
 * now announces what the release projection says about it, in the session's
 * language.
 *
 * `unavailable` in `coogo-ide-chrome-copy.ts` stays where it is and keeps its
 * own meaning ("unavailable until review passes"). It is not reused here:
 * Launch is no longer gated on the editor preflight, so a phrase about review
 * passing would describe a gate this surface no longer consults.
 *
 * Typography per language, not one ASCII join for all five — ja/zh/zh-TW take
 * the full-width parenthetical, ko the half-width one, en the em dash. Same
 * rule the chrome copy's `unavailable` follows.
 */
export interface ReleaseJourneyCopy {
  /** Accessible name for a step the release projection shows finished. */
  completed: (label: string) => string;
  /** Accessible name for a step whose work has not produced its artifact. */
  notCompleted: (label: string) => string;
  /** Accessible name for Launch while the release cannot be launched. */
  launchUnavailable: (label: string) => string;
}

const en: ReleaseJourneyCopy = {
  completed: (label) => `${label} — done`,
  notCompleted: (label) => `${label} — not done yet`,
  launchUnavailable: (label) =>
    `${label} — unavailable until a candidate is ready to launch`,
};

const ko: ReleaseJourneyCopy = {
  completed: (label) => `${label}(완료됨)`,
  notCompleted: (label) => `${label}(아직 완료되지 않음)`,
  launchUnavailable: (label) => `${label}(배포할 후보본이 준비되어야 열립니다)`,
};

const ja: ReleaseJourneyCopy = {
  completed: (label) => `${label}（完了）`,
  notCompleted: (label) => `${label}（未完了）`,
  launchUnavailable: (label) =>
    `${label}（リリースできる候補が用意されるまで使えません）`,
};

const zh: ReleaseJourneyCopy = {
  completed: (label) => `${label}（已完成）`,
  notCompleted: (label) => `${label}（尚未完成）`,
  launchUnavailable: (label) => `${label}（准备好可上线的候选后才能使用）`,
};

const zhTW: ReleaseJourneyCopy = {
  completed: (label) => `${label}（已完成）`,
  notCompleted: (label) => `${label}（尚未完成）`,
  launchUnavailable: (label) => `${label}（準備好可上線的候選後才能使用）`,
};

export const RELEASE_JOURNEY_COPY: Record<LocaleCode, ReleaseJourneyCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getReleaseJourneyCopy(locale: LocaleCode): ReleaseJourneyCopy {
  return RELEASE_JOURNEY_COPY[locale] ?? en;
}
